import { z } from 'zod';

import { type BriefContent, briefContentSchemaV2 } from './brief.ts';
import { DomainError } from './errors.ts';
import { type GoalContent, goalContentSchema } from './goal.ts';
import {
  type MaterialsContent,
  liftMaterialsV1,
  materialsContentSchema,
  materialsContentV1Schema,
} from './materials.ts';

/**
 * One mechanism carries every artifact: research questions, discussion guide, audience, the
 * study's Materials, its Goal and its Brief. Kind selects the content schema; everything else about
 * versioning, approval and staleness is kind-agnostic. `answer` and `finding` are retired kinds:
 * see `RETIRED_ARTIFACT_KINDS`.
 */
export const ARTIFACT_KINDS = [
  'rqs',
  'dg',
  'audience',
  'answer',
  'finding',
  'materials',
  'goal',
  'brief',
] as const;
export const artifactKindSchema = z.enum(ARTIFACT_KINDS);
export type ArtifactKind = z.infer<typeof artifactKindSchema>;

/**
 * Kinds the store reads and refuses to write, on every path (`artifact_kind_retired`).
 *
 * They are in `ARTIFACT_KINDS` because that list is the Postgres `artifact_kind` enum, which
 * cannot drop a value while rows, an index predicate and a trigger variable name it; their rules
 * and content schemas are kept so a stored row reads.
 */
export const RETIRED_ARTIFACT_KINDS = ['answer', 'finding'] as const;
export type RetiredArtifactKind = (typeof RETIRED_ARTIFACT_KINDS)[number];

const RETIRED: readonly ArtifactKind[] = RETIRED_ARTIFACT_KINDS;

export const isRetiredArtifactKind = (kind: ArtifactKind): kind is RetiredArtifactKind =>
  RETIRED.includes(kind);

/**
 * The approval order, and therefore the staleness order: a change to a kind marks
 * every kind after it in this chain stale. It is also what a purchase snapshots.
 */
export const DRAFT_ARTIFACT_CHAIN = ['rqs', 'dg', 'audience'] as const;
export type DraftArtifactKind = (typeof DRAFT_ARTIFACT_CHAIN)[number];

const CHAIN: readonly ArtifactKind[] = DRAFT_ARTIFACT_CHAIN;

export const isDraftArtifactKind = (kind: ArtifactKind): kind is DraftArtifactKind =>
  CHAIN.includes(kind);

/**
 * What a change to a kind makes stale: every chain kind after it, and for the Goal and the
 * Materials the whole chain.
 *
 * The Goal and the Materials are upstream of the chain without being in it: a changed Goal redrafts
 * the research questions, and changed Materials have them reconciled, and either leaves the guide
 * and audience waiting on it, so each of the three is noted against the change directly rather than
 * only through the kind in front of it. Neither is in the chain because the chain is also what a
 * purchase snapshots. The Goal is never itself made stale: changed Materials leave it chosen, and
 * reach the chain directly, so a corrected fact never redrafts the research questions from scratch.
 */
export const downstreamArtifactKinds = (kind: ArtifactKind): readonly DraftArtifactKind[] => {
  if (kind === 'goal' || kind === 'materials') {
    return DRAFT_ARTIFACT_CHAIN;
  }

  const position = CHAIN.indexOf(kind);
  return position === -1 ? [] : DRAFT_ARTIFACT_CHAIN.slice(position + 1);
};

/**
 * Every kind a staleness window can name: the Goal, the Materials, then the chain, in the order a
 * change travels. The order is the order windows are stored in.
 */
export const STALE_UPSTREAM_KINDS = ['goal', 'materials', ...DRAFT_ARTIFACT_CHAIN] as const;
export type StaleUpstreamKind = (typeof STALE_UPSTREAM_KINDS)[number];

const UPSTREAM: readonly ArtifactKind[] = STALE_UPSTREAM_KINDS;

/**
 * What a study holds exactly one of, and what launch and a purchase hold freeze, for every kind.
 *
 * The draft chain is both, and so are the Materials and the Goal, which sit outside the chain
 * because they are neither part of readiness nor of what a purchase snapshots. A study has one
 * Brief, written after fielding and never held. The retired kinds are many per study and not held.
 * A record over every kind rather than a list of the held ones, so a new kind cannot be added without
 * deciding both: a kind the freeze did not know about would otherwise be writable on a launched
 * study without anything saying so.
 */
const KIND_RULES: Record<ArtifactKind, { onePerStudy: boolean; held: boolean }> = {
  rqs: { onePerStudy: true, held: true },
  dg: { onePerStudy: true, held: true },
  audience: { onePerStudy: true, held: true },
  materials: { onePerStudy: true, held: true },
  goal: { onePerStudy: true, held: true },
  answer: { onePerStudy: false, held: false },
  finding: { onePerStudy: false, held: false },
  brief: { onePerStudy: true, held: false },
};

const rulesFor = (kind: ArtifactKind): { onePerStudy: boolean; held: boolean } => {
  const rules = Object.hasOwn(KIND_RULES, kind) ? KIND_RULES[kind] : undefined;
  if (rules === undefined) {
    // Only reachable from a value the type system was not consulted about, which is our bug.
    throw new Error(`no artifact kind ${String(kind)}`);
  }

  return rules;
};

/** The kinds launch freezes and a purchase holds: the draft chain, the Materials and the Goal. */
export const HELD_ARTIFACT_KINDS = [...DRAFT_ARTIFACT_CHAIN, 'materials', 'goal'] as const;
export type HeldArtifactKind = (typeof HELD_ARTIFACT_KINDS)[number];

/** Whether launch freezes this kind and a purchase holds it. Refuses a kind it does not know. */
export const isHeldArtifactKind = (kind: ArtifactKind): kind is HeldArtifactKind =>
  rulesFor(kind).held;

/** Whether a study holds exactly one of this kind, which the store then finds from the study. */
export const isOnePerStudyArtifactKind = (kind: ArtifactKind): boolean =>
  rulesFor(kind).onePerStudy;

export const ARTIFACT_STATUSES = ['unapproved', 'approved', 'stale'] as const;
export const artifactStatusSchema = z.enum(ARTIFACT_STATUSES);
export type ArtifactStatus = z.infer<typeof artifactStatusSchema>;

export const AUTHOR_KINDS = ['user', 'agent'] as const;
export const authorKindSchema = z.enum(AUTHOR_KINDS);
export type AuthorKind = z.infer<typeof authorKindSchema>;

/**
 * One unresolved upstream change: the two upstream versions bracket the diff the agent proposes, so
 * "this affects questions 5–6" is computed from stored content rather than guessed.
 */
export const stalenessWindowSchema = z.object({
  upstream_kind: z.enum(STALE_UPSTREAM_KINDS),
  from_version: z.int().positive(),
  to_version: z.int().positive(),
});
export type StalenessWindow = z.infer<typeof stalenessWindowSchema>;

export const STALE_REASON_SCHEMA_VERSION = 2;

/**
 * Why a downstream artifact needs attention: **one window per upstream kind**, not one window.
 *
 * The audience is downstream of the Goal, the Materials, the research questions and the guide, so it
 * can be waiting on four different changes at once; the bound is one window for every kind a window
 * can name. A single window meant the second cascade overwrote the first, and the change it dropped
 * was by definition one nobody had addressed yet — so the artifact stopped asking about it while
 * still being wrong about it. Keeping a window per kind is what lets a revision say "these two
 * things changed" and what lets each be resolved on its own evidence.
 *
 * Versioned because it is stored jsonb that outlives the code that wrote it.
 */
export const staleReasonSchema = z
  .object({
    schema_version: z.literal(STALE_REASON_SCHEMA_VERSION),
    windows: z.array(stalenessWindowSchema).min(1).max(STALE_UPSTREAM_KINDS.length),
  })
  .superRefine((reason, context) => {
    const kinds = reason.windows.map((window) => window.upstream_kind);
    if (new Set(kinds).size !== kinds.length) {
      context.addIssue({
        code: 'custom',
        path: ['windows'],
        // Two windows for one kind would make "resolve the guide change" ambiguous about which of
        // them it answered, and the merge below exists precisely so it cannot happen.
        message: 'one window per upstream kind',
      });
    }
  });
export type StaleReason = z.infer<typeof staleReasonSchema>;

/**
 * Validate on read, like artifact content: the cascade branches on this payload and the
 * agent's minimal-diff proposal consumes it, so a shape written by older code has to fail
 * loudly here rather than quietly widen the wrong window.
 */
export const parseStaleReason = (value: unknown): StaleReason | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsed = staleReasonSchema.safeParse(value);
  if (parsed.success) {
    return parsed.data;
  }

  // Always a read of something already stored, so the fault is ours, not the caller's.
  throw new DomainError('stored_content_invalid', 'stored stale_reason does not match its schema', {
    issues: z.prettifyError(parsed.error),
  });
};

/**
 * The same read, for a caller that must not throw.
 *
 * `parseStaleReason` blames us loudly for a payload we stored, which is right on the server: the
 * request fails and somebody sees it. In a React render there is no error boundary above the sidebar,
 * so the same throw takes down every artifact on screen over one bad field. Answering `null` loses
 * the needs-attention flag for that one artifact instead, which is the smaller of the two harms and
 * the same trade the event reader already makes for a malformed frame.
 */
export const tryParseStaleReason = (value: unknown): StaleReason | null => {
  try {
    return parseStaleReason(value);
  } catch {
    return null;
  }
};

export const stalenessWindowsOf = (reason: StaleReason | null): readonly StalenessWindow[] =>
  reason === null ? [] : reason.windows;

/**
 * Windows back into a payload, in chain order, or `null` for "nothing outstanding".
 *
 * `null` rather than an empty list is the representation the column and `deriveArtifactStatus`
 * already use for "not stale", and having exactly one way to say it is what stops a row that reads
 * as stale from carrying nothing to resolve. Upstream order is imposed here so the payload of a study
 * does not depend on which change happened to arrive first.
 */
export const staleReasonOf = (windows: readonly StalenessWindow[]): StaleReason | null => {
  if (windows.length === 0) {
    return null;
  }

  return {
    schema_version: STALE_REASON_SCHEMA_VERSION,
    windows: windows.toSorted(
      (left, right) => UPSTREAM.indexOf(left.upstream_kind) - UPSTREAM.indexOf(right.upstream_kind),
    ),
  };
};

/**
 * Records another upstream change against an artifact.
 *
 * A repeat from the same kind **widens** the existing window — the older starting point is kept, so
 * the diff still spans every change the user has not yet seen rather than only the most recent one.
 * A different kind is **added** alongside, which is the whole point of the shape.
 */
export const noteStalenessWindow = (
  reason: StaleReason | null,
  window: StalenessWindow,
): StaleReason => {
  const existing = stalenessWindowsOf(reason);
  const sameKind = existing.find((candidate) => candidate.upstream_kind === window.upstream_kind);

  const merged =
    sameKind === undefined
      ? [...existing, window]
      : existing.map((candidate) =>
          candidate.upstream_kind === window.upstream_kind
            ? { ...candidate, to_version: window.to_version }
            : candidate,
        );

  // Non-null by construction: `merged` always has at least the window just noted.
  return staleReasonOf(merged) as StaleReason;
};

/**
 * Drops the windows a write says it has reconciled, and keeps the rest.
 *
 * This is the half that makes per-kind windows worth having. If any write cleared everything, an
 * artifact stale from two changes could be brought back to "fine" by a revision that addressed one
 * of them — the same silent loss as the overwrite, one step later. So a writer names what it
 * answered, an unnamed window survives, and the artifact keeps asking until each change has been
 * accounted for separately. Naming a kind that is not outstanding is not an error: it is the honest
 * answer to a race in which someone else resolved it first.
 *
 * Returns the *same* payload when nothing named was outstanding, and callers rely on that: whether
 * anything has to be written at all is decided by comparing the result with what went in, so a
 * freshly built copy of an unchanged payload would report every no-op save as a change.
 */
export const resolveStalenessWindows = (
  reason: StaleReason | null,
  resolved: readonly StaleUpstreamKind[],
): StaleReason | null => {
  const windows = stalenessWindowsOf(reason);
  const remaining = windows.filter((window) => !resolved.includes(window.upstream_kind));

  return remaining.length === windows.length ? reason : staleReasonOf(remaining);
};

/**
 * What each document is called in front of a user.
 *
 * Two vocabularies, deliberately. `rqs`/`dg`/`audience` are what the agent's tools take as arguments
 * and what the digest renders, so precision beats prose there. But a refusal's message is shown to
 * the researcher verbatim — the chat surface echoes any terminal code it does not have its own
 * wording for, and the sidebar shows a failed action's message as-is — and "rqs" is exactly the
 * platform vocabulary the product promises never to put in front of them.
 */
export const DRAFT_ARTIFACT_WORDING: Record<DraftArtifactKind, string> = {
  rqs: 'the research questions',
  dg: 'the discussion guide',
  audience: 'the audience',
};

/** The same wording for every kind a staleness window can name. */
export const UPSTREAM_WORDING: Record<StaleUpstreamKind, string> = {
  goal: 'the goal',
  materials: 'the materials',
  ...DRAFT_ARTIFACT_WORDING,
};

/** The same wording for every kind launch freezes. */
export const HELD_ARTIFACT_WORDING: Record<HeldArtifactKind, string> = {
  ...DRAFT_ARTIFACT_WORDING,
  materials: 'the materials',
  goal: 'the goal',
};

/** The outstanding changes, in words a researcher reads rather than the tools' own names. */
export const describeStaleReasonForUser = (reason: StaleReason | null): string => {
  const named = stalenessWindowsOf(reason).map((window) => UPSTREAM_WORDING[window.upstream_kind]);

  if (named.length === 0) {
    return '';
  }

  // "A and B" rather than a list of one item with a stray conjunction.
  return named.length === 1
    ? (named[0] as string)
    : `${named.slice(0, -1).join(', ')} and ${named.at(-1) as string}`;
};

/**
 * What a researcher is told about a document they cannot approve because it is waiting on a change
 * above it: the document is updated for the change on its own card, so the repair is waiting for
 * that update rather than asking anyone for it. Every surface that says so reads it from here.
 *
 * `kind` names the document and `reason` the change when the caller has them; without them it
 * reads as "this" and "a change above it". The research questions are plural and read so.
 */
export const staleApprovalText = (
  kind: ArtifactKind | null,
  reason: StaleReason | null,
): string => {
  const draft = kind !== null && isDraftArtifactKind(kind) ? kind : null;
  const plural = draft === 'rqs';
  const subject = `${plural ? 'These' : 'This'}${draft === null ? '' : ` ${DRAFT_ARTIFACT_WORDING[draft].replace(/^the /u, '')}`}`;
  const named = describeStaleReasonForUser(reason);
  const change = named === '' ? 'a change above it' : `a change to ${named}`;

  return `${subject} ${plural ? 'are' : 'is'} being updated for ${change}. Approve ${plural ? 'them' : 'it'} once the update shows on ${plural ? 'their' : 'its'} card.`;
};

/**
 * The one comparison of named brackets against what is stored, shared by both ways of using it.
 *
 * Windows are named by their exact brackets rather than by kind because a kind cannot say *which*
 * version of a change was addressed: clearing by kind retires whatever is outstanding at write time,
 * including an edit that landed after the claimant last looked. So a named window either matches what
 * is stored exactly and comes off, or it does not match and is reported back unmatched — and the two
 * callers below differ only in what they do about that, which is the whole reason this returns the
 * mismatch instead of deciding.
 *
 * Pure, so the store can call it inside the transaction that writes — the only place the comparison is
 * worth anything, since a check made beforehand can be overtaken by an upstream edit committing in
 * between.
 *
 * The remaining payload is the *same reference* when no window came off, matching
 * `resolveStalenessWindows`, because callers decide whether anything resolved by comparing the result
 * with what went in. A rebuilt copy of an unchanged payload reports a write that cleared nothing as
 * having cleared a change.
 */
const compareNamedWindows = (
  reason: StaleReason | null,
  named: readonly StalenessWindow[],
): {
  remaining: StaleReason | null;
  unmatched: readonly { named: StalenessWindow; stored: StalenessWindow | null }[];
} => {
  const outstanding = new Map(
    stalenessWindowsOf(reason).map((window) => [window.upstream_kind, window]),
  );
  const before = outstanding.size;
  const unmatched: { named: StalenessWindow; stored: StalenessWindow | null }[] = [];

  for (const window of named) {
    const stored = outstanding.get(window.upstream_kind);

    if (stored === undefined) {
      unmatched.push({ named: window, stored: null });
      continue;
    }

    if (stored.from_version !== window.from_version || stored.to_version !== window.to_version) {
      unmatched.push({ named: window, stored });
      continue;
    }

    outstanding.delete(window.upstream_kind);
  }

  return {
    remaining: outstanding.size === before ? reason : staleReasonOf([...outstanding.values()]),
    unmatched,
  };
};

/**
 * Clears the windows a regenerated document claims, and leaves the rest outstanding.
 *
 * What an agent's own write gets. A claim it cannot honour is dropped rather than refused, because the
 * document is the expensive thing here: a turn that rewrites the research questions and then the guide
 * has itself widened the guide's window in between, and refusing the guide would throw away a whole
 * generated document over a bracket the write is not even trying to change. Dropping the claim keeps
 * the invariant that matters — a window nobody has demonstrably addressed stays open, so the artifact
 * keeps asking — while the version precondition remains the hard gate that protects the *content*
 * somebody wrote.
 *
 * Nothing here can widen or invent a window, so the worst outcome is a flag that stays up until it is
 * claimed against the bracket that is really outstanding.
 */
export const claimStalenessWindows = (
  reason: StaleReason | null,
  claimed: readonly StalenessWindow[],
): StaleReason | null => compareNamedWindows(reason, claimed).remaining;

/**
 * Resolves windows named by their exact brackets, refusing anything that has moved.
 *
 * What a *proposal* gets, where refusing is the product behaviour: a proposal was computed against
 * particular versions, possibly minutes ago, and it is shown to a user as "this is what changed and
 * here is my fix". If the upstream moved after that, the honest answer is a fresh suggestion, not a
 * quietly narrower one — so unlike a claim, an unmatched window fails the whole call.
 *
 * Both refusals are deliberate and different, because the wrong one misleads: a window that is *gone*
 * was settled by something else, and telling the user their change "moved on" would be false; a
 * window that is *wider* is the case this exists for.
 */
export const resolveExactStalenessWindows = (
  reason: StaleReason | null,
  answered: readonly StalenessWindow[],
): StaleReason | null => {
  const { remaining, unmatched } = compareNamedWindows(reason, answered);
  const [refused] = unmatched;

  if (refused === undefined) {
    return remaining;
  }

  if (refused.stored === null) {
    throw new DomainError(
      'artifact_not_stale',
      'That change has already been settled, so there is nothing left to answer here.',
      { upstream_kind: refused.named.upstream_kind, answered_window: refused.named },
    );
  }

  throw new DomainError(
    'artifact_version_not_current',
    'That change moved on after this was suggested, so ask for a fresh suggestion.',
    {
      upstream_kind: refused.named.upstream_kind,
      answered_window: refused.named,
      stored_window: refused.stored,
    },
  );
};

/** The outstanding changes in words, for a tool result and for the digest the agent reads. */
export const describeStaleReason = (reason: StaleReason | null): string =>
  stalenessWindowsOf(reason)
    .map(
      (window) =>
        `${window.upstream_kind} changed from version ${String(window.from_version)} to ${String(window.to_version)}`,
    )
    .join('; ');

/** Approval is a pointer at a version, so it survives later edits instead of blocking them. */
export const deriveArtifactStatus = ({
  currentVersion,
  approvedVersion,
  staleReason,
}: {
  currentVersion: number;
  approvedVersion: number | null;
  staleReason: StaleReason | null;
}): ArtifactStatus => {
  if (staleReason !== null) {
    return 'stale';
  }

  return approvedVersion === currentVersion ? 'approved' : 'unapproved';
};

const innerIdSchema = z.uuid();

/**
 * Upper bounds on stored content. `min(1)` alone leaves every field unbounded, and this
 * content is written into an append-only table: an oversized document cannot be deleted
 * later, and parsing one costs the process that also serves the SPA. The limits are far
 * above anything a real study produces — they exist to make a runaway generation or a
 * hostile request fail at the edge rather than become permanent.
 */
const MAX_TEXT = 4000;
const MAX_TITLE = 500;
const MAX_ITEMS = 200;
const MAX_OPTIONS = 100;
const MAX_SECTIONS = 50;
const MAX_QUESTIONS_PER_SECTION = 100;

/** The most research questions one stored set holds. */
export const MAX_RESEARCH_QUESTIONS = MAX_ITEMS;

const boundedText = (max: number = MAX_TEXT) => z.string().min(1).max(max);

/**
 * Inner ids survive versions — an RQ, a DG section, a DG question and a poll option
 * each keep their id across every rewrite — which is what lets answers cite an RQ
 * and DG questions declare which RQs they serve. Two items sharing an id would make
 * those references ambiguous, so uniqueness is part of the schema.
 */
const requireUniqueIds = (
  items: readonly { id: string }[],
  path: (string | number)[],
  context: z.RefinementCtx,
): void => {
  const seen = new Set<string>();
  items.forEach((item, index) => {
    if (seen.has(item.id)) {
      context.addIssue({
        code: 'custom',
        path: [...path, index, 'id'],
        message: `duplicate id ${item.id}`,
      });
    }
    seen.add(item.id);
  });
};

export const researchQuestionSchemaV1 = z.object({
  id: innerIdSchema,
  text: boundedText(),
  /** The decision the RQ informs; the PRD treats an RQ with no decision as unfinished. */
  decision: boundedText().optional(),
});

export const rqsContentSchemaV1 = z
  .object({
    items: z.array(researchQuestionSchemaV1).min(1).max(MAX_ITEMS),
  })
  .superRefine((content, context) => {
    requireUniqueIds(content.items, ['items'], context);
  });
export type RqsContentV1 = z.infer<typeof rqsContentSchemaV1>;

/** Whose words a research question is in: Sigora's draft, or the researcher's own rewording. */
export const RQ_WORDED_BY = ['sigora', 'user'] as const;
export type RqWordedBy = (typeof RQ_WORDED_BY)[number];

/**
 * The chosen Goal a set of research questions decomposes: the Goal version and the option chosen
 * in it. A reference rather than a copy, because Goal versions are append-only and so the version
 * always resolves, and because the store compares it with the study's current Goal on every write.
 */
export const rqsGoalRefSchema = z.strictObject({
  version: z.int().positive(),
  option_id: z.uuid(),
});
export type RqsGoalRef = z.infer<typeof rqsGoalRefSchema>;

export const researchQuestionSchemaV2 = z.strictObject({
  id: innerIdSchema,
  text: boundedText(),
  /** What part of the Goal's decision this question informs: the card's "Informs" line. */
  decision: boundedText(),
  /** `user` once the researcher has reworded it, which the card shows as "Your wording". */
  worded_by: z.enum(RQ_WORDED_BY),
});

/**
 * The research questions every write stores: the Goal they decompose, and each question with the
 * decision it informs and whose wording it is in. Strict, so a key this shape does not name is
 * refused rather than silently dropped from a version nobody can rewrite.
 */
export const rqsContentSchemaV2 = z
  .strictObject({
    goal: rqsGoalRefSchema,
    items: z.array(researchQuestionSchemaV2).min(1).max(MAX_RESEARCH_QUESTIONS),
  })
  .superRefine((content, context) => {
    requireUniqueIds(content.items, ['items'], context);
  });

/**
 * One research question as every reader sees it, whichever version stored it. `decision` is empty
 * only on a question read from a version-1 row that had none.
 */
export interface ResearchQuestion {
  id: string;
  text: string;
  decision: string;
  worded_by: RqWordedBy;
}

/**
 * The research questions as every reader sees them. `goal` is `null` only on a version-1 row, which
 * was written before the questions named the Goal they came from; the store refuses to write one.
 */
export interface RqsContent {
  goal: RqsGoalRef | null;
  items: ResearchQuestion[];
}

/** A version-1 row in the shape every reader takes: no Goal, and every question in Sigora's words. */
export const liftRqsV1 = (content: RqsContentV1): RqsContent => ({
  goal: null,
  items: content.items.map((item) => ({
    id: item.id,
    text: item.text,
    decision: item.decision ?? '',
    worded_by: 'sigora',
  })),
});

/**
 * Research questions of any stored version, normalised to `RqsContent`, for a reader that has the
 * content without its `schema_version` — the SPA's artifact read. The current shape is tried first,
 * because version 1 is not strict and would accept a current document with its new keys stripped.
 */
export const rqsContentSchema: z.ZodType<RqsContent> = z.union([
  rqsContentSchemaV2,
  rqsContentSchemaV1.transform(liftRqsV1),
]);

/**
 * Sigora's own plain-language question types. The integration layer translates them
 * into Remesh guide types when it builds the conversation, so a Remesh rename never
 * reaches stored content.
 */
export const DG_QUESTION_TYPES = ['open_response', 'single_choice', 'multiple_choice'] as const;
export const dgQuestionTypeSchema = z.enum(DG_QUESTION_TYPES);
export type DgQuestionType = z.infer<typeof dgQuestionTypeSchema>;

/**
 * The question types an agent-authored guide may hold: everything but multiple-choice. A guide can
 * still be read with a `multiple_choice` question, and the Remesh conversion still translates one,
 * but no drafter, reshape or proposal writes one — the store refuses it as
 * `dg_question_kind_unavailable` until several-answer polls are supported end to end.
 */
export const GENERATED_DG_QUESTION_TYPES = ['open_response', 'single_choice'] as const;
export type GeneratedDgQuestionType = (typeof GENERATED_DG_QUESTION_TYPES)[number];

/** Whose words a guide question and its options are in: Sigora's draft, or the researcher's own. */
export const DG_WORDED_BY = ['sigora', 'user'] as const;
export type DgWordedBy = (typeof DG_WORDED_BY)[number];

/** Options iff the question is not open, and no option id twice; shared by every version. */
const refineDgQuestion = (
  question: { type: DgQuestionType; options?: readonly { id: string }[] | undefined },
  context: z.RefinementCtx,
): void => {
  const needsOptions = question.type !== 'open_response';
  if (needsOptions && question.options === undefined) {
    context.addIssue({
      code: 'custom',
      path: ['options'],
      message: `a ${question.type} question needs options`,
    });
  }

  if (!needsOptions && question.options !== undefined) {
    context.addIssue({
      code: 'custom',
      path: ['options'],
      message: 'an open_response question has no options',
    });
  }

  requireUniqueIds(question.options ?? [], ['options'], context);
};

/** Section ids unique, and question ids unique across the whole guide; shared by every version. */
const refineDgContent = (
  content: { sections: readonly { id: string; questions: readonly { id: string }[] }[] },
  context: z.RefinementCtx,
): void => {
  requireUniqueIds(content.sections, ['sections'], context);

  // Question ids are unique across the whole guide, not just within a section:
  // answers and the rewording path address a question by id alone.
  const questions = content.sections.flatMap((section) => section.questions);
  requireUniqueIds(questions, ['sections'], context);
};

export const dgOptionSchemaV1 = z.object({
  id: innerIdSchema,
  text: boundedText(),
});

export const dgQuestionSchemaV1 = z
  .object({
    id: innerIdSchema,
    type: dgQuestionTypeSchema,
    text: boundedText(),
    options: z.array(dgOptionSchemaV1).min(2).max(MAX_OPTIONS).optional(),
    serves_rq_ids: z.array(innerIdSchema).min(1).max(MAX_ITEMS),
  })
  .superRefine(refineDgQuestion);

export const dgSectionSchemaV1 = z.object({
  id: innerIdSchema,
  title: boundedText(MAX_TITLE),
  questions: z.array(dgQuestionSchemaV1).min(1).max(MAX_QUESTIONS_PER_SECTION),
});

/** The guide as version 1 stored it: no wording marker, and a duration the agent wrote. */
export const dgContentSchemaV1 = z
  .object({
    sections: z.array(dgSectionSchemaV1).min(1).max(MAX_SECTIONS),
    estimated_duration_minutes: z.number().positive().optional(),
  })
  .superRefine(refineDgContent);
export type DgContentV1 = z.infer<typeof dgContentSchemaV1>;

export const dgOptionSchema = z.strictObject({
  id: innerIdSchema,
  text: boundedText(),
});
export type DgOption = z.infer<typeof dgOptionSchema>;

/** The fields a question has in version 2, which version 3 keeps beside the image it shows. */
const dgQuestionFieldsV2 = {
  id: innerIdSchema,
  type: dgQuestionTypeSchema,
  text: boundedText(),
  /** A poll's answers, in the order participants see them. */
  options: z.array(dgOptionSchema).min(2).max(MAX_OPTIONS).optional(),
  /** Traceability: every question earns its place by serving at least one RQ. */
  serves_rq_ids: z.array(innerIdSchema).min(1).max(MAX_ITEMS),
  /**
   * `user` once the researcher has reworded the question or any of its options, which the
   * Guide step shows as "Your wording". Set only by the server, never by a model.
   */
  worded_by: z.enum(DG_WORDED_BY),
};

export const dgQuestionSchemaV2 = z.strictObject(dgQuestionFieldsV2).superRefine(refineDgQuestion);

export const dgSectionSchemaV2 = z.strictObject({
  id: innerIdSchema,
  title: boundedText(MAX_TITLE),
  questions: z.array(dgQuestionSchemaV2).min(1).max(MAX_QUESTIONS_PER_SECTION),
});

/** The guide as version 2 stored it: version 3 without the image a question shows. */
export const dgContentSchemaV2 = z
  .strictObject({
    sections: z.array(dgSectionSchemaV2).min(1).max(MAX_SECTIONS),
  })
  .superRefine(refineDgContent);
export type DgContentV2 = z.infer<typeof dgContentSchemaV2>;

/**
 * The image a question shows, immediately before the question itself: one of the study's Stimulus
 * candidates, pinned to the Materials version it was chosen from. The file is the stored row, so
 * the same id in a later Materials version is the same bytes.
 */
export const dgStimulusSchema = z.strictObject({
  materials_artifact_id: innerIdSchema,
  materials_version: z.int().positive(),
  file_id: innerIdSchema,
});
export type DgStimulus = z.infer<typeof dgStimulusSchema>;

/** What a researcher is told when two of a question's options are worded alike. */
export const DG_DUPLICATE_OPTION_MESSAGE =
  'Two options in this question have the same wording. Give each one its own.';

/**
 * An option's wording as two options are told apart: trimmed, each run of whitespace one space. A
 * participant's choice comes back from Remesh as the chosen option's text, so two options this
 * reads alike could not be told apart once answered. Held where a guide is written and approved
 * (`assertDgOptionsWordedApart`), not where one is read, so a stored guide always loads.
 */
export const dgOptionWording = (text: string): string => text.trim().replace(/\s+/g, ' ');

export const dgQuestionSchemaV3 = z
  .strictObject({
    ...dgQuestionFieldsV2,
    /** The image participants see just before this question, if it shows one. */
    stimulus: dgStimulusSchema.optional(),
  })
  .superRefine(refineDgQuestion);
export type DgQuestion = z.infer<typeof dgQuestionSchemaV3>;

export const dgSectionSchemaV3 = z.strictObject({
  id: innerIdSchema,
  title: boundedText(MAX_TITLE),
  questions: z.array(dgQuestionSchemaV3).min(1).max(MAX_QUESTIONS_PER_SECTION),
});
export type DgSection = z.infer<typeof dgSectionSchemaV3>;

/** No image shown on two questions: each is shown once, with the question asked about it. */
const refineDgStimuli = (
  content: { sections: readonly { questions: readonly { stimulus?: DgStimulus | undefined }[] }[] },
  context: z.RefinementCtx,
): void => {
  const seen = new Set<string>();
  for (const question of content.sections.flatMap((section) => section.questions)) {
    const fileId = question.stimulus?.file_id;
    if (fileId === undefined) {
      continue;
    }

    if (seen.has(fileId)) {
      context.addIssue({
        code: 'custom',
        path: ['sections'],
        message: `image ${fileId} is shown on more than one question`,
      });
    }
    seen.add(fileId);
  }
};

/**
 * The guide every write stores: sections of questions, each question saying which research
 * questions it serves, whose words it is in and which image, if any, it shows. Strict, so a key
 * this shape does not name is refused rather than silently dropped from a version nobody can
 * rewrite. It carries no duration: minutes are computed from the questions by `guideMinutes`, so
 * no stored number can disagree with the one the price is worked out from.
 */
export const dgContentSchemaV3 = z
  .strictObject({
    sections: z.array(dgSectionSchemaV3).min(1).max(MAX_SECTIONS),
  })
  .superRefine(refineDgContent)
  .superRefine(refineDgStimuli);
export type DgContent = z.infer<typeof dgContentSchemaV3>;

/** A version-1 row in the shape every reader takes: every question in Sigora's words, no minutes. */
export const liftDgV1 = (content: DgContentV1): DgContent => ({
  sections: content.sections.map((section) => ({
    id: section.id,
    title: section.title,
    questions: section.questions.map((question) => ({
      id: question.id,
      type: question.type,
      text: question.text,
      ...(question.options === undefined
        ? {}
        : { options: question.options.map((option) => ({ id: option.id, text: option.text })) }),
      serves_rq_ids: question.serves_rq_ids,
      worded_by: 'sigora' as const,
    })),
  })),
});

/**
 * A version-2 row in the shape every reader takes, which it already is: a version-2 question is a
 * version-3 question that shows no image. It adds no key, so an unchanged save over a version-2 row
 * hashes the same and writes nothing.
 */
export const liftDgV2 = (content: DgContentV2): DgContent => content;

/**
 * A guide of any stored version, normalised to `DgContent`, for a reader that has the content
 * without its `schema_version` — the SPA's artifact read. The current shape is tried first,
 * because version 1 is not strict and would accept a current document with its new keys stripped.
 */
export const dgContentSchema: z.ZodType<DgContent> = z.union([
  dgContentSchemaV3,
  dgContentSchemaV2.transform(liftDgV2),
  dgContentSchemaV1.transform(liftDgV1),
]);

export const AUDIENCE_FILTER_MODES = ['targeting', 'capture_only'] as const;
export const audienceFilterModeSchema = z.enum(AUDIENCE_FILTER_MODES);
export type AudienceFilterMode = z.infer<typeof audienceFilterModeSchema>;

const audienceFilterFields = {
  id: innerIdSchema,
  dimension: z.string().min(1),
  /**
   * `capture_only` selects every option, or a range's whole span, so the filter records the
   * demographic without narrowing recruitment.
   */
  mode: audienceFilterModeSchema,
};

/**
 * Inclusive whole-number bounds, lower first. Only their order is asked here: which bounds a
 * demographic takes is the panel's, asked by the translation against the catalog.
 */
export const audienceRangeSchema = z
  .object({ lower: z.int().min(0), upper: z.int().min(0) })
  .refine((range) => range.lower <= range.upper, {
    message: 'a range’s lower bound is at most its upper bound',
    path: ['upper'],
  });
export type AudienceRange = z.infer<typeof audienceRangeSchema>;

/** A filter on a demographic the panel offers as options: the options it selects. */
export interface AudienceSelectFilter {
  id: string;
  dimension: string;
  mode: AudienceFilterMode;
  selected_options: string[];
}

/** A filter on a demographic the panel offers as bounds (age): the span it selects. */
export interface AudienceRangeFilter {
  id: string;
  dimension: string;
  mode: AudienceFilterMode;
  range: AudienceRange;
}

export type AudienceFilter = AudienceSelectFilter | AudienceRangeFilter;

/**
 * A panel filter: the options it selects or the span it selects, exactly one of the two. Each is
 * checked as its own field, so an empty `selected_options` or a reversed `range` is refused under
 * that field's path, and a key a filter does not name is stripped. Both or neither is refused on
 * the filter itself. A stored filter parses into itself, so an unchanged save over it writes
 * nothing.
 */
export const audienceFilterSchema: z.ZodType<AudienceFilter> = z
  .object({
    ...audienceFilterFields,
    selected_options: z.array(z.string().min(1)).min(1).optional(),
    range: audienceRangeSchema.optional(),
  })
  .refine((filter) => (filter.selected_options === undefined) !== (filter.range === undefined), {
    message: 'a filter gives exactly one of selected_options and range',
  })
  .transform(({ id, dimension, mode, selected_options, range }): AudienceFilter =>
    range === undefined
      ? { id, dimension, mode, selected_options: selected_options ?? [] }
      : { id, dimension, mode, range },
  );

/**
 * The one demographic an audience can target as a range. Remesh's recruit endpoints refuse a range
 * on any other, so the translation refuses one and every render marks it as not recruitable.
 */
export const TARGETABLE_RANGE_DIMENSION_ID = 'age';

export const isRangeFilter = (filter: AudienceFilter): filter is AudienceRangeFilter =>
  'range' in filter;

/** What a filter selects, under its own key: `{ selected_options }` or `{ range }`. */
export const filterSelection = (
  filter: AudienceFilter,
): Pick<AudienceSelectFilter, 'selected_options'> | Pick<AudienceRangeFilter, 'range'> =>
  isRangeFilter(filter) ? { range: filter.range } : { selected_options: filter.selected_options };

/** A range as every read-only render shows it: its bounds joined by an en dash, `25–44`. */
export const audienceRangeText = (range: AudienceRange): string =>
  `${String(range.lower)}–${String(range.upper)}`;

/**
 * A version-1 filter: options only, and not strict, as version 1 was written. Read only through
 * the version-1 lift.
 */
const audienceFilterSchemaV1 = z.object({
  ...audienceFilterFields,
  selected_options: z.array(z.string().min(1)).min(1),
});

/**
 * The smallest N an audience's content may carry.
 *
 * Arithmetic, not advice: below this a study is not a study. The smallest study worth reading is
 * the Quality floor (`QUALITY_FLOOR` in `quality.ts`), which every write, approval, purchase and
 * provisioning refuses below — and which is deliberately not a schema minimum, so a later, higher
 * floor never makes a stored audience, or a launched study's Brief, unreadable.
 */
export const AUDIENCE_MIN_COMPLETES = 8;

/**
 * Version 1 of the audience: a description, N, the panel filters and free-text caveats. Lifted on
 * read into the current shape (`liftAudienceV1`).
 */
export const audienceContentSchemaV1 = z
  .object({
    /** Plain language is the primary display; the filter list is the honest contract. */
    description: z.string().min(1),
    target_completes: z.int().min(AUDIENCE_MIN_COMPLETES),
    filters: z.array(audienceFilterSchemaV1),
    /** Where targeting cannot express the intent, the artifact says so. */
    caveats: z.array(z.string().min(1)),
  })
  .superRefine((content, context) => {
    requireUniqueIds(content.filters, ['filters'], context);
  });
export type AudienceContentV1 = z.infer<typeof audienceContentSchemaV1>;

/**
 * The countries an audience can recruit in, in the order they are stored. Participants take part in
 * English, the one language the conversion allows, so language is not a field.
 */
export const COUNTRIES = ['UK', 'US'] as const;
export const countrySchema = z.enum(COUNTRIES);
export type Country = z.infer<typeof countrySchema>;

export const AUDIENCE_WORDED_BY = ['sigora', 'user'] as const;
export type AudienceWordedBy = (typeof AUDIENCE_WORDED_BY)[number];

/** One answer to a screening question, and whether choosing it ends the survey. */
export const audienceScreenerOptionSchema = z.strictObject({
  id: innerIdSchema,
  text: boundedText(),
  terminates: z.boolean(),
});
export type AudienceScreenerOption = z.infer<typeof audienceScreenerOptionSchema>;

/**
 * A screening question Sigora wrote: single-select, asked before the conversation, and ending the
 * survey for anyone who chooses an option marked `terminates`. Its structure — the options, their
 * order and which of them turn people away — is Sigora's; the researcher may reword the question
 * and its options, which marks it `user`.
 *
 * The bounds here are storage bounds. How many screeners and options an audience may carry, and how
 * long each may be, is policy, asked on every write (`assertAudienceStructure`), so a change to it
 * never makes a stored audience unreadable.
 */
export const audienceScreenerSchema = z
  .strictObject({
    id: innerIdSchema,
    text: boundedText(),
    options: z.array(audienceScreenerOptionSchema).min(2).max(MAX_OPTIONS),
    worded_by: z.enum(AUDIENCE_WORDED_BY),
  })
  .superRefine((screener, context) => {
    requireUniqueIds(screener.options, ['options'], context);
  });
export type AudienceScreener = z.infer<typeof audienceScreenerSchema>;

/** The longest targeting gap Sigora may record: a sentence or two. */
export const MAX_TARGETING_GAP_CHARS = 300;

/** What the targeting cannot express, in Sigora's words. */
export const targetingGapSchema = z.strictObject({
  id: innerIdSchema,
  text: boundedText(MAX_TARGETING_GAP_CHARS),
});
export type TargetingGap = z.infer<typeof targetingGapSchema>;

/** Each constraint the pipeline puts on who can take part, in the order the store writes them. */
export const PIPELINE_CONSTRAINT_KEYS = [
  'language',
  'countries',
  'simulated_participants',
] as const;
export type PipelineConstraintKey = (typeof PIPELINE_CONSTRAINT_KEYS)[number];

/** A limit the pipeline imposes, derived by the store from the audience and the environment. */
export const pipelineConstraintSchema = z.strictObject({
  key: z.enum(PIPELINE_CONSTRAINT_KEYS),
  text: boundedText(),
});
export type PipelineConstraint = z.infer<typeof pipelineConstraintSchema>;

/**
 * Everything the audience says it cannot do, in two kinds that are written differently: a
 * targeting gap is authored by Sigora where neither a filter nor a screener can express the intent,
 * and a pipeline constraint is derived by the store on every write (`pipelineConstraints`), so its
 * wording is never model output. Stored inside the audience, so every guarantee the audience has —
 * append-only history, the purchase hold, the launch freeze — holds the wording the researcher
 * accepted.
 */
export const audienceLimitationsSchema = z
  .strictObject({
    targeting_gaps: z.array(targetingGapSchema).max(MAX_ITEMS),
    pipeline_constraints: z.array(pipelineConstraintSchema).max(PIPELINE_CONSTRAINT_KEYS.length),
  })
  .superRefine((limitations, context) => {
    requireUniqueIds(limitations.targeting_gaps, ['targeting_gaps'], context);
    const keys = new Set<string>();
    limitations.pipeline_constraints.forEach((constraint, index) => {
      if (keys.has(constraint.key)) {
        context.addIssue({
          code: 'custom',
          path: ['pipeline_constraints', index, 'key'],
          message: `duplicate pipeline constraint ${constraint.key}`,
        });
      }
      keys.add(constraint.key);
    });
  });
export type AudienceLimitations = z.infer<typeof audienceLimitationsSchema>;

/**
 * The audience every write stores: who (`description`), how many (`target_completes`), where
 * (`countries`, stored sorted with no repeat), the panel filters, Sigora's screening questions and
 * the audience's Limitations. Strict, so a key this shape does not name is refused rather than
 * silently dropped.
 *
 * N is bounded here only by `AUDIENCE_MIN_COMPLETES`; the Quality floor is a domain rule.
 */
export const audienceContentSchemaV2 = z
  .strictObject({
    description: boundedText(MAX_TITLE),
    target_completes: z.int().min(AUDIENCE_MIN_COMPLETES),
    countries: z.array(countrySchema).min(1).max(COUNTRIES.length),
    filters: z.array(audienceFilterSchema).max(MAX_ITEMS),
    screeners: z.array(audienceScreenerSchema).max(MAX_ITEMS),
    limitations: audienceLimitationsSchema,
  })
  .superRefine((content, context) => {
    requireUniqueIds(content.filters, ['filters'], context);
    requireUniqueIds(content.screeners, ['screeners'], context);
    const sorted = [...new Set(content.countries)].toSorted();
    if (
      sorted.length !== content.countries.length ||
      sorted.some((country, index) => country !== content.countries[index])
    ) {
      context.addIssue({
        code: 'custom',
        path: ['countries'],
        message: 'countries are stored sorted, each once',
      });
    }
  });
export type AudienceContent = z.infer<typeof audienceContentSchemaV2>;

/**
 * The fixed prefix of a lifted targeting gap's id. A version-1 caveat has no id, so the lift makes
 * one from its position, the same on every read.
 */
const LIFTED_GAP_ID_PREFIX = '00000000-0000-4000-8000-';

/** The id a lifted caveat reads with: deterministic, so lifting a row twice gives the same ids. */
export const liftedGapId = (index: number): string =>
  `${LIFTED_GAP_ID_PREFIX}${index.toString(16).padStart(12, '0')}`;

/**
 * A version-1 row in the shape every reader takes: recruited in both countries, no screeners, and
 * each caveat a targeting gap. Its pipeline constraints are empty, because none was ever written
 * for it; the next write derives them.
 */
export const liftAudienceV1 = (content: AudienceContentV1): AudienceContent => ({
  description: content.description,
  target_completes: content.target_completes,
  countries: [...COUNTRIES],
  filters: content.filters,
  screeners: [],
  limitations: {
    targeting_gaps: content.caveats.map((text, index) => ({ id: liftedGapId(index), text })),
    pipeline_constraints: [],
  },
});

/**
 * An audience of any stored version, normalised to `AudienceContent`, for a reader that has the
 * content without its `schema_version` — the SPA's artifact read. The current shape is tried
 * first, because version 1 is not strict and would accept a current document with its new keys
 * stripped.
 */
export const audienceContentSchema: z.ZodType<AudienceContent> = z.union([
  audienceContentSchemaV2,
  audienceContentSchemaV1.transform(liftAudienceV1),
]);

/**
 * The stored shapes of the retired `answer` and `finding` kinds, kept so a stored row still reads.
 * A sentence that made a claim carries the `claim_key` its evidence was recorded under.
 */
export const answerClaimSchema = z.object({
  text: z.string().min(1),
  claim_key: z.string().min(1).optional(),
});
export type AnswerClaim = z.infer<typeof answerClaimSchema>;

export const answerQuantRowSchema = z.object({
  label: z.string().min(1),
  count: z.int().nonnegative(),
  agreement: z.number().optional(),
});

export const answerQuantBlockSchema = z.object({
  id: innerIdSchema,
  dg_question_id: innerIdSchema,
  headline: z.string().min(1),
  rows: z.array(answerQuantRowSchema).min(1),
});
export type AnswerQuantBlock = z.infer<typeof answerQuantBlockSchema>;

export const answerContentSchema = z
  .object({
    rq_id: innerIdSchema,
    headline: z.string().min(1),
    narrative: z.array(answerClaimSchema).min(1),
    quant_blocks: z.array(answerQuantBlockSchema),
    insufficient_evidence: z.boolean(),
    diagnosis: z.string().min(1).optional(),
    suggested_follow_up: z.string().min(1).optional(),
  })
  .superRefine((content, context) => {
    if (!content.insufficient_evidence) {
      return;
    }

    for (const field of ['diagnosis', 'suggested_follow_up'] as const) {
      if (content[field] === undefined) {
        context.addIssue({
          code: 'custom',
          path: [field],
          message: `insufficient evidence requires ${field}`,
        });
      }
    }
  });
export type AnswerContent = z.infer<typeof answerContentSchema>;

export const findingContentSchema = z.object({
  title: z.string().min(1),
  prompt: z.string().min(1),
  narrative: z.array(answerClaimSchema).min(1),
});
export type FindingContent = z.infer<typeof findingContentSchema>;

/**
 * The all-in price, snapshotted with the inputs that produced it: what `studies.quote` holds once a
 * study is locked. An issued quote carries its id and the end of its guarantee window; a study
 * locked with no purchase behind it (the dev freeze, a runtime check's run) is locked on a local
 * estimate, which has neither.
 */
export const quoteSchema = z.object({
  quote_id: z.uuid().optional(),
  total_cents: z.int().nonnegative(),
  currency: z.string().length(3),
  target_completes: z.int().positive(),
  estimated_duration_minutes: z.number().positive(),
  computed_at: z.iso.datetime(),
  expires_at: z.iso.datetime().optional(),
});
export type Quote = z.infer<typeof quoteSchema>;

/**
 * A quote Sigora issued and will honour until `expires_at`: one row of `study_quotes`, as a
 * purchase stores it and the quote route answers it. Strict, with every key required, so nothing a
 * purchase was bound to is stripped on its way out of the API.
 */
export const issuedQuoteSchema = z.strictObject({
  quote_id: z.uuid(),
  total_cents: z.int().nonnegative(),
  currency: z.literal('USD'),
  target_completes: z.int().positive(),
  estimated_duration_minutes: z.number().positive(),
  computed_at: z.iso.datetime(),
  expires_at: z.iso.datetime(),
});
export type IssuedQuote = z.infer<typeof issuedQuoteSchema>;

/**
 * What Launch is shown as the price. `accepted: false` is a live issued quote the researcher may
 * still accept; `accepted: true` is the price a pending or paid purchase (or a lock) already holds
 * the study at, which no window lapses.
 */
export const quoteViewSchema = z.discriminatedUnion('accepted', [
  z.strictObject({ quote: issuedQuoteSchema, accepted: z.literal(false) }),
  z.strictObject({ quote: quoteSchema, accepted: z.literal(true) }),
]);
export type QuoteView = z.infer<typeof quoteViewSchema>;

/**
 * The `schema_version` every write of a kind is stamped and parsed with. Bumped for one kind when
 * its content shape changes incompatibly; old rows keep their own number, and `CONTENT_SCHEMAS`
 * keeps reading them.
 */
export const CURRENT_SCHEMA_VERSIONS: Record<ArtifactKind, number> = {
  rqs: 2,
  dg: 3,
  audience: 2,
  answer: 1,
  finding: 1,
  materials: 2,
  goal: 1,
  brief: 2,
};

export interface ArtifactContentByKind {
  rqs: RqsContent;
  dg: DgContent;
  audience: AudienceContent;
  answer: AnswerContent;
  finding: FindingContent;
  materials: MaterialsContent;
  goal: GoalContent;
  brief: BriefContent;
}

/**
 * Every readable shape of every kind, keyed by the `schema_version` stored beside
 * the content. Stored jsonb outlives the code that wrote it, so a new shape adds an
 * entry here rather than replacing one — that is what keeps old versions renderable.
 */
const CONTENT_SCHEMAS: Record<ArtifactKind, Record<number, z.ZodType>> = {
  rqs: { 1: rqsContentSchemaV1.transform(liftRqsV1), 2: rqsContentSchemaV2 },
  dg: {
    1: dgContentSchemaV1.transform(liftDgV1),
    2: dgContentSchemaV2.transform(liftDgV2),
    3: dgContentSchemaV3,
  },
  audience: { 1: audienceContentSchemaV1.transform(liftAudienceV1), 2: audienceContentSchemaV2 },
  answer: { 1: answerContentSchema },
  finding: { 1: findingContentSchema },
  materials: { 1: materialsContentV1Schema.transform(liftMaterialsV1), 2: materialsContentSchema },
  goal: { 1: goalContentSchema },
  // Only the Brief's current shape is read: no Brief written in an earlier one is kept.
  brief: { 2: briefContentSchemaV2 },
};

export const parseArtifactContent = <Kind extends ArtifactKind>(
  kind: Kind,
  schemaVersion: number,
  content: unknown,
): ArtifactContentByKind[Kind] => {
  const schema = CONTENT_SCHEMAS[kind][schemaVersion];
  if (schema === undefined) {
    throw new DomainError(
      'unknown_schema_version',
      `no schema for ${kind} content at schema_version ${String(schemaVersion)}`,
      { kind, schema_version: schemaVersion },
    );
  }

  const parsed = schema.safeParse(content);
  if (!parsed.success) {
    throw new DomainError('invalid_content', `invalid ${kind} content`, {
      kind,
      schema_version: schemaVersion,
      issues: z.prettifyError(parsed.error),
    });
  }

  // The registry is keyed by kind, which the return type follows, but a lookup in a
  // `Record<ArtifactKind, …>` of erased schemas cannot express that to the compiler.
  return parsed.data as ArtifactContentByKind[Kind];
};

/**
 * The same parse, for content coming back out of the database. A stored row that no longer
 * matches its schema is our bug — a bad migration, a hand-edited row, a shape someone changed
 * without adding a `schema_version` — so it must not answer with the code that blames the
 * caller for sending bad content.
 */
export const parseStoredArtifactContent = <Kind extends ArtifactKind>(
  kind: Kind,
  schemaVersion: number,
  content: unknown,
): ArtifactContentByKind[Kind] => {
  try {
    return parseArtifactContent(kind, schemaVersion, content);
  } catch (error) {
    if (error instanceof DomainError && error.code === 'invalid_content') {
      throw new DomainError('stored_content_invalid', `stored ${kind} content no longer parses`, {
        ...error.detail,
      });
    }

    throw error;
  }
};

/**
 * Just the ids out of a stored `rqs` row, without judging the rest of it.
 *
 * Deliberately not `parseStoredArtifactContent`: every guide and answer write has to know which
 * research questions exist, and running the full registry parse to learn that makes an unrelated
 * write fail on a row it never touches. A row written by a newer image, at a `schema_version` this
 * one has no schema for, is the realistic way that happens — an ordinary rollback, answered with a
 * 500 the runtime reports as the model having broken. What a reference check actually needs is the
 * ids, and every version of this shape has carried them in the same place.
 *
 * Still a parse rather than a cast, because the column is `unknown` and a malformed row must fail
 * as our bug rather than silently read as "this study has no research questions", which would
 * refuse every write against it with a message blaming the caller.
 */
const storedRqIdsSchema = z.object({
  /**
   * At least one, because every version of this shape has required one: no `rqs` schema has
   * ever accepted an empty list, so a row that parses to none is a corrupt row rather than a study
   * without research questions. Answering `[]` for it would be the silent misreading this refuses —
   * indistinguishable from having no `rqs` at all, and every write against it would then be told, in
   * the caller's own words, that it named research questions the study does not have.
   *
   * The id is checked for being a non-empty string and not for being a uuid, deliberately: the
   * format is the part a future shape might reasonably change, and an id in some other format simply
   * fails to match a reference, which is a resolvable answer rather than a broken row.
   */
  items: z.array(z.object({ id: z.string().min(1) })).min(1),
});

export const rqIdsFromStoredContent = (content: unknown): string[] => {
  const parsed = storedRqIdsSchema.safeParse(content);

  if (!parsed.success) {
    throw new DomainError('stored_content_invalid', 'stored rqs content no longer parses', {
      kind: 'rqs',
      issues: z.prettifyError(parsed.error),
    });
  }

  return parsed.data.items.map((item) => item.id);
};
